import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { SHALE_PROFILE_SOURCE } from "constants/datasource.constants";
import { RootState } from "store/rootReducer";

import {
  ACCESSIBLE_DATA_SOURCES_QUERY_KEY,
  getAccessibleDataSources
} from "api/dataSource";

export const useNoviAccess = () => {
  // Optional-chained: this hook now runs from useInitialUserContextLoader, which mounts on the
  // sign-in page where state.auth.user is null (authSlice.ts:22) — a non-optional read here
  // would throw before sign-in.
  const orgId = useSelector((state: RootState) => state.auth.user?.organization?.id);
  const { data, isSuccess } = useQuery(
    [ACCESSIBLE_DATA_SOURCES_QUERY_KEY, orgId],
    () => getAccessibleDataSources(orgId),
    { enabled: !!orgId }
  );

  // getAccessibleDataSources catches every failure and resolves { ok: false } rather than
  // rejecting, so react-query always reports success and `isError` can never be true here.
  // That makes a 5xx/timeout look identical to "this org has no Novi", so the only trustworthy
  // signal is whether the fetch actually succeeded.
  //
  // There used to be a second, weaker flag here — `isResolved` (the request settled, whatever
  // it said) — offered for deciding when a stored value could be APPLIED, with isAuthoritative
  // reserved for DOWNGRADING one. Splitting those axes is what let a soft failure apply a
  // Novi-dependent value literally: ready to apply, not authoritative enough to narrow. Both
  // decisions take isAuthoritative now, and the weaker flag is gone so it cannot be reached
  // for again. (EVA-180)
  const isAuthoritative = isSuccess && data?.ok === true;
  const dataSources = isAuthoritative ? data.value : [];

  return {
    isAuthoritative,
    hasNoviAccess: dataSources.some((source) => source.source === SHALE_PROFILE_SOURCE)
  };
};

export const useHasNoviAccess = () => useNoviAccess().hasNoviAccess;
